import { useState } from 'react';
import { Plus, FileText, Upload, StickyNote, Search } from 'lucide-react';
import { useDocuments } from '@/hooks/useDocuments';
import { useDocumentsPageData } from '@/hooks/useDocumentsPageData';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { DocumentItem } from '@/components/documents/DocumentItem';
import { EditDocumentModal } from '@/components/documents/EditDocumentModal';
import { CategoryFolderCard } from '@/components/documents/CategoryFolderCard';
import { CategoryDocumentsModal } from '@/components/documents/CategoryDocumentsModal';
import { ConditionalLoading } from '@/components/ConditionalLoading';
import { useModalNavigation } from '@/hooks/useModalNavigation';
import { Input } from '@/components/ui/input';
import { ModernHero } from '@/components/ModernHero';
import documentsHeroImage from '@/assets/tasks-hero.jpg';
import { useFamily } from '@/hooks/useFamily';
import { NoFamilyMessage } from '@/components/NoFamilyMessage';

import { useLocalModal } from "@/hooks/useLocalModal";

export default function MobileDocuments() {
  const { family } = useFamily();
  
  // Pre-fetch documents data in a single RPC call
  const { data: docsPageData, isLoading: docsPageLoading } = useDocumentsPageData(family?.id);
  
  // Use individual hook that reads from pre-populated cache
  const { documents, loading, refreshDocuments } = useDocuments();
  const { openModal } = useModalNavigation();
  const { setLocalModalOpen } = useLocalModal();
  const [selectedCategory, setSelectedCategory] = useState<'medical' | 'identity' | 'school' | 'insurance' | 'other' | null>(null);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleDocumentClick = (documentId: string) => {
    openModal('edit_document', { id: documentId });
  };

  const handleCategoryClick = (category: 'medical' | 'identity' | 'school' | 'insurance' | 'other') => {
    setSelectedCategory(category);
    setIsCategoryModalOpen(true);
    setLocalModalOpen(true);
  };

  const handleCloseCategoryModal = () => {
    setSelectedCategory(null);
    setIsCategoryModalOpen(false);
    setLocalModalOpen(false);
  };

  const categoryLabels = {
    medical: 'Médical',
    identity: 'Identité',
    school: 'École',
    insurance: 'Assurance',
    other: 'Autre'
  };

  // Filter documents based on search query
  const filteredDocuments = documents.filter(doc => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    const titleMatch = doc.title?.toLowerCase().includes(query);
    const contentMatch = doc.content?.toLowerCase().includes(query);
    const categoryMatch = doc.category?.toLowerCase().includes(query);
    
    return titleMatch || contentMatch || categoryMatch;
  });

  // Separate notes from filtered documents
  const notes = filteredDocuments.filter(doc => doc.document_type === 'note');
  const regularDocuments = filteredDocuments.filter(doc => doc.document_type === 'document');
  
  const groupedDocuments = regularDocuments.reduce((groups, doc) => {
    const category = doc.category || 'other';
    if (!groups[category]) groups[category] = [];
    groups[category].push(doc);
    return groups;
  }, {} as Record<string, typeof regularDocuments>);

  const heroActions = [
    {
      icon: Upload,
      label: 'Ajouter un document',
      onClick: () => openModal('create_document'),
      className: 'bg-violet-pastel hover:bg-violet-pastel/90 text-violet-pastel-foreground'
    },
    {
      icon: StickyNote,
      label: 'Créer une note',
      onClick: () => openModal('create_note'),
      className: 'border-violet-pastel text-violet-pastel hover:bg-violet-pastel hover:text-violet-pastel-foreground'
    }
  ];

  if (!family) {
    return (
      <NoFamilyMessage 
        description="Vous devez d'abord créer une famille pour organiser vos documents"
        buttonText="Créer ma famille"
        variant="documents"
      />
    );
  }

  return (
    <>
      <div className="min-h-screen bg-page-gradient">
        <ModernHero
          title="Documents"
          actions={heroActions}
          backgroundImage={documentsHeroImage}
          category="documents"
          allowImageChange={true}
          height="40vh"
        />

        {/* Content Section */}
        <div className="relative -mt-24 bg-content-warm rounded-t-3xl pt-8 px-4 min-h-fit pb-28 z-20">
          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher dans les titres, contenus et catégories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <ConditionalLoading>
            {documents.length === 0 ? (
              <div className="text-center py-16 px-4">
                <div className="w-24 h-24 mx-auto mb-6 bg-violet-pastel/20 rounded-full flex items-center justify-center">
                  <FileText className="h-12 w-12 text-violet-pastel" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Aucun document</h3>
                <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                  Commencez par ajouter vos premiers documents ou créer des notes pour votre famille.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button 
                    onClick={() => openModal('create_document')}
                    className="bg-violet-pastel hover:bg-violet-pastel/90 text-violet-pastel-foreground"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Ajouter un document
                  </Button>
                  <Button 
                    onClick={() => openModal('create_note')}
                    variant="outline"
                    className="border-violet-pastel text-violet-pastel hover:bg-violet-pastel hover:text-violet-pastel-foreground"
                  >
                    <StickyNote className="h-4 w-4 mr-2" />
                    Créer une note
                  </Button>
                </div>
              </div>
            ) : filteredDocuments.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">Aucun résultat trouvé pour "{searchQuery}"</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Notes section */}
                {notes.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">📝</span>
                      <h2 className="text-xl font-semibold text-foreground">Notes</h2>
                      <span className="text-muted-foreground">({notes.length})</span>
                    </div>
                    <div className="space-y-1">
                      {notes.map((note) => (
                        <DocumentItem
                          key={note.id}
                          document={note}
                          onClick={() => handleDocumentClick(note.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Documents folders */}
                {Object.keys(groupedDocuments).length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">📁</span>
                      <h2 className="text-xl font-semibold text-foreground">Documents</h2>
                      <span className="text-muted-foreground">({Object.values(groupedDocuments).flat().length})</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      {Object.entries(groupedDocuments).map(([category, categoryDocuments]) => (
                        <CategoryFolderCard
                          key={category}
                          category={category as 'medical' | 'identity' | 'school' | 'insurance' | 'other'}
                          documents={categoryDocuments}
                          onClick={() => handleCategoryClick(category as 'medical' | 'identity' | 'school' | 'insurance' | 'other')}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </ConditionalLoading>
        </div>
      </div>

      {/* Modals - only local ones not handled by global system */}
      <CategoryDocumentsModal
        isOpen={isCategoryModalOpen}
        onClose={handleCloseCategoryModal}
        category={selectedCategory}
        documents={selectedCategory ? groupedDocuments[selectedCategory] || [] : []}
        onDocumentClick={handleDocumentClick}
      />
    </>
  );
}